import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { AlertTriangle, Mail, RefreshCw } from 'lucide-react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { fmtDateTime } from '../../lib/timezone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BADGE = { sent: 'bg-emerald-100 text-emerald-800', mocked: 'bg-amber-100 text-amber-800', failed: 'bg-red-100 text-red-800' };

const LogRow = ({ row, onAction }) => (
  <div className={`border rounded-md p-2.5 text-sm ${row.status === 'failed' && !row.acknowledged ? 'border-red-300 bg-red-50/50' : ''}`} data-testid={`booking-email-row-${row.id}`}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <div className="flex flex-wrap items-center gap-2 min-w-0">
        <span className={`text-[11px] rounded-full px-2 py-0.5 font-medium ${BADGE[row.status] || 'bg-slate-100'}`} data-testid={`booking-email-status-${row.id}`}>{row.status}</span>
        {row.status === 'failed' && row.acknowledged && (
          <span className="text-[11px] rounded-full px-2 py-0.5 bg-slate-100 text-slate-600" data-testid={`booking-email-handled-${row.id}`}>{row.resent_as ? 'resent' : 'dismissed'}</span>
        )}
        <span className="text-xs text-muted-foreground capitalize">{row.kind} · to {row.role}</span>
        <span className="truncate">{(row.to || []).join(', ') || '—'}</span>
      </div>
      <span className="text-xs text-muted-foreground">{fmtDateTime(row.created_at)}</span>
    </div>
    <p className="text-xs text-muted-foreground mt-1 truncate">{row.subject}{row.channel ? ` · via ${row.channel}` : ''}{row.attempts > 1 ? ` · ${row.attempts} attempts` : ''}</p>
    {row.status === 'failed' && (
      <div className="mt-1.5 flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-red-700 break-words flex-1" data-testid={`booking-email-error-${row.id}`}>{row.error}</p>
        {!row.acknowledged && (
          <div className="flex gap-1">
            <Button size="sm" variant="outline" onClick={() => onAction(row.id, 'resend')} data-testid={`booking-email-resend-${row.id}`}><RefreshCw size={12} className="mr-1" /> Resend</Button>
            <Button size="sm" variant="ghost" onClick={() => onAction(row.id, 'dismiss')} data-testid={`booking-email-dismiss-${row.id}`}>Dismiss</Button>
          </div>
        )}
      </div>
    )}
  </div>
);

export const BookingEmailLog = () => {
  const [data, setData] = useState(null);
  const [onlyFailed, setOnlyFailed] = useState(false);
  const load = () => axios.get(`${API}/admin/booking-emails`, { params: onlyFailed ? { status: 'failed' } : {} })
    .then((r) => setData(r.data)).catch(() => setData({ items: [], unresolved_failures: 0 }));
  useEffect(() => { load(); }, [onlyFailed]); // eslint-disable-line

  const onAction = async (id, action) => {
    try {
      const r = await axios.post(`${API}/admin/booking-emails/${id}/${action}`);
      if (action === 'resend') {
        if (r.data.status === 'failed') toast.error(`Resend failed: ${r.data.error}`);
        else toast.success('Email resent');
      }
    } catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    load();
  };
  if (!data) return null;
  return (
    <Card className="p-5 space-y-3" data-testid="booking-email-log-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="font-semibold flex items-center gap-2"><Mail size={18} className="text-primary" /> Booking email log</h3>
        <div className="flex items-center gap-3">
          <label className="text-xs flex items-center gap-1.5"><input type="checkbox" checked={onlyFailed} onChange={(e) => setOnlyFailed(e.target.checked)} data-testid="booking-email-only-failed" /> Failed only</label>
          <Button size="sm" variant="ghost" onClick={load} data-testid="booking-email-refresh"><RefreshCw size={13} /></Button>
        </div>
      </div>
      {data.unresolved_failures > 0 && (
        <p className="text-sm text-red-700 flex items-center gap-2 bg-red-50 border border-red-200 rounded-md p-2" data-testid="booking-email-failure-banner">
          <AlertTriangle size={15} /> {data.unresolved_failures} booking email{data.unresolved_failures > 1 ? 's' : ''} failed to send. Resend or dismiss below.
        </p>
      )}
      <div className="space-y-2 max-h-[420px] overflow-y-auto">
        {data.items.map((row) => <LogRow key={row.id} row={row} onAction={onAction} />)}
        {data.items.length === 0 && <p className="text-sm text-muted-foreground">No booking emails yet.</p>}
      </div>
    </Card>
  );
};
